import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface ShopUsage {
  shop_name: string;
  count: number;
}

interface UsageCounter {
  current: number;
  limit: number | null;
  per_shop?: Record<string, ShopUsage>;
}

interface Plan {
  id: string;
  name: string;
  display_name: string;
  description: string;
  price_monthly: number;
  price_yearly: number;
  currency: string;
  max_products: number | null;
  max_employees: number | null;
  max_warehouses: number | null;
  max_shops: number | null;
  features: string[];
}

interface Subscription {
  id: string;
  status:
    | 'active'
    | 'pending_payment'
    | 'pending_validation'
    | 'expired'
    | 'suspended'
    | 'free'
    | 'cancelled'
    | 'rejected';
  status_label: string;
  billing_cycle: string;
  starts_at: string;
  ends_at: string;
  plan: Plan;
}

interface SubscriptionAlert {
  code: string;
  severity: 'info' | 'warning' | 'critical';
  title: string;
  message: string;
  cta_url: string | null;
}

interface QuoteRequestSummary {
  id: string;
  company_name: string;
  business_sector: string;
  status: 'pending' | 'responded' | 'accepted' | 'rejected' | 'expired';
  admin_response: string | null;
  proposed_plan_id: string | null;
  proposed_price_monthly: number | null;
  proposed_price_yearly: number | null;
  proposed_overrides: Record<string, number | null> | null;
  proposed_plan: {
    id: string;
    name: string;
    display_name: string;
    description: string;
    price_monthly: number;
    price_yearly: number;
    currency: string;
    max_products: number | null;
    max_employees: number | null;
    max_shops: number | null;
  } | null;
  expires_at: string | null;
  created_at: string;
}

interface SubscriptionData {
  subscription: Subscription | null;
  usage: {
    shops: UsageCounter;
    products: UsageCounter;
    employees: UsageCounter;
    warehouses: UsageCounter;
  };
  days_remaining: number;
  access_state: string;
  blocking: boolean;
  write_restricted: boolean;
  alerts: SubscriptionAlert[];
  quote_request: QuoteRequestSummary | null;
}

export function useSubscription() {
  return useQuery({
    queryKey: ['subscription', 'current'],
    queryFn: async () => {
      const response = await api.get<SubscriptionData>('/subscriptions/current');
      return response.data;
    },
  });
}

export type {
  SubscriptionData,
  Subscription,
  SubscriptionAlert,
  Plan,
  UsageCounter,
  ShopUsage,
  QuoteRequestSummary,
};
